// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Una lavorazione dall'inizio alla fine"
// Centro assistenza → guida per operatori e amministratori, solo piano TRACE.
//
// È la guida più lunga del Centro assistenza, e la più delicata: descrive la
// schermata su cui abbiamo lavorato di più fra luglio e agosto 2026.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Schermata ............. App.js, GestioneLavorazioni (lista + dettaglio)
//   Avvio fase ............ apriModalAvvio → modale "▶ Avvio fase: <nome>"
//   Chiusura fase ......... apriModalChiusura → "✓ Chiusura fase: <nome>",
//                           oppure "🏁 Chiusura Lavorazione" se è l'ultima.
//                           IL PULSANTE SI CHIAMA "Avvia fase successiva →":
//                           un gesto solo chiude la fase corrente e apre la
//                           successiva. Se il nome cambia, il capitolo 5 va
//                           allineato.
//   Posizioni prodotto .... riquadro "📍 Posizioni prodotto" sulla fase in corso
//   Sposta / Scarta ....... "📦 Sposta prodotto" / "🗑️ Scarta prodotto"
//   Chiusura lavorazione .. "✅ Chiusura lavorazione" → sezioni "📦 Dati finali
//                           lavorazione" e "📍 Dove resta il prodotto"
//   Regole di chiusura .... backend/routes/tracciabilita.js: guard ITEM E (fasi
//                           mancanti / più fasi aperte, 409), drenaggio delle
//                           ubicazioni con residuo a SCARTO, 409 "operazione
//                           non ripetibile" se non è APERTA
//   Annullamento .......... "🚫 Annulla lavorazione", solo se APERTA, con
//                           motivazione obbligatoria
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'l0',  label: 'Prima di iniziare' },
  { id: 'l1',  label: '1. Aprire la lavorazione' },
  { id: 'l2',  label: '2. Scegliere i lotti' },
  { id: 'l3',  label: '3. Avviare la prima fase' },
  { id: 'l4',  label: '4. Dov’è il prodotto adesso' },
  { id: 'l5',  label: '5. Passare alla fase dopo' },
  { id: 'l6',  label: '6. Le quantità usate' },
  { id: 'l7',  label: '7. Chiudere la lavorazione' },
  { id: 'l8',  label: '8. Se qualcosa va storto' },
  { id: 'l9',  label: 'Le trappole' },
  { id: 'l10', label: 'Domande frequenti' },
];

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    blu:    { bg: LT.blu,   fg: '#fff',  bd: LT.blu },
    neutro: { bg: LT.n0,    fg: LT.n700, bd: LT.n300 },
    verde:  { bg: LT.verde, fg: '#fff',  bd: LT.verde },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '5px 11px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, placeholder, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || placeholder}</div>
  </div>
);

export default function Lavorazione() {
  return (
    <GuidaLayout
      titolo="Una lavorazione dall'inizio alla fine"
      sottotitolo="Aprirla, scegliere i lotti, portarla avanti fase per fase e chiuderla dichiarando dove resta il prodotto. È il cuore della tracciabilità: tutto il resto serve a rendere possibile questo."
      durata="~ 25 minuti"
      destinatario="Per operatori e amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* l0 */}
      <H id="l0">Prima di iniziare</H>

      <P>
        Una lavorazione è <Strong>una produzione</Strong>: la giornata in cui fate il salame, la
        caldaia di latte che diventa caciotta. Ha un lotto, una data, degli ingredienti che
        entrano e un prodotto che esce.
      </P>
      <P>
        Il gesto che conta, in tutta questa schermata, è uno solo: <Strong>dire quali lotti
        state usando</Strong>. Tutto il resto — le fasi, le ubicazioni, le quantità — serve a
        rendere quel legame verificabile. Se un giorno bisogna richiamare un prodotto, è da lì
        che si parte.
      </P>

      <Callout kind="info" titolo="Cosa deve esistere prima">
        La <Strong>ricetta</Strong> del prodotto, e i <Strong>lotti in magazzino</Strong> degli
        ingredienti che userete, cioè almeno una bolla di ingresso registrata. Senza ricetta non
        si apre una lavorazione; senza lotti si apre ma non c'è niente da scegliere.
      </Callout>

      <P>
        Una lavorazione ha tre stati: <Strong>Aperta</Strong> mentre ci lavorate,{' '}
        <Strong>Chiusa</Strong> quando avete finito, <Strong>Annullata</Strong> se l'avete aperta
        per sbaglio. Vale la pena saperlo subito: <Strong>una lavorazione chiusa non si
        riapre</Strong>, e il programma ve lo dice con "operazione non ripetibile". Non è una
        durezza inutile — un registro di produzione modificabile a posteriori non dimostrerebbe
        niente.
      </P>

      {/* l1 */}
      <H id="l1" cat="produzione">1. Aprire la lavorazione</H>

      <P>
        Da <Code>Lavorazioni</Code>, il pulsante <Tasto>Nuova lavorazione</Tasto>. Si sceglie il{' '}
        <Strong>prodotto</Strong> e poi la <Strong>ricetta</Strong> — se il prodotto ne ha più di
        una, qui si decide quale seguire.
      </P>
      <P>
        Il <Strong>Lotto prodotto</Strong> arriva già compilato nel formato{' '}
        <Code>L.GGMMAA</Code>, derivato dalla data. È un campo libero: se in azienda avete una
        vostra codifica, scrivetela. E se cambiate la data di inizio, il lotto proposto si
        aggiorna solo se non l'avevate già personalizzato.
      </P>
      <P>
        La <Strong>Quantità prevista</Strong> è quanto contate di produrre <em>questa volta</em>.
        Da qui nasce la riscalatura: se la ricetta è scritta su 100 kg e voi ne fate 250, tutte
        le dosi vengono proposte moltiplicate per 2,5.
      </P>

      <Callout kind="tip" titolo="Se lavorate per conto terzi">
        C'è un campo <Strong>"Committente (conto lavorazione)"</Strong>, facoltativo, con il
        valore predefinito "— lavorazione standard —". Va compilato quando producete per conto di
        qualcun altro: resta agganciato alla lavorazione e vi permette di distinguerla dalle
        vostre.
      </Callout>

      {/* l2 */}
      <H id="l2" cat="temperature">2. Scegliere i lotti</H>

      <P>
        Nel dettaglio della lavorazione c'è il riquadro <Strong>"🗂 Disponibilità
        ingredienti"</Strong>. Per ogni ingrediente della ricetta il programma mostra la
        quantità che serve — già riscalata — e quali lotti avete a magazzino.
      </P>
      <P>
        Un pallino colorato dice a colpo d'occhio come sta messo ciascun ingrediente: verde se
        c'è un lotto <Strong>in uso</Strong>, arancione se ce n'è uno solo in magazzino da
        mettere in uso, rosso se non c'è niente.
      </P>
      <P>
        Sotto ogni lotto trovate la <Strong>"~ disponibile stimata"</Strong>: quanto dovrebbe
        restarne, contando quello che le altre lavorazioni hanno dichiarato di aver consumato.
        È una stima, non un inventario — nessuno pesa il magazzino ogni sera — e serve a
        capire se il lotto basta o conviene aprirne un altro. Quando non basta, il programma
        propone da sé il lotto successivo con un pulsante per segnare esaurito il vecchio e
        attivare il nuovo.
      </P>

      <Callout kind="warn" titolo="È questo il momento che crea la tracciabilità">
        Scegliere il lotto giusto è il gesto più importante di tutta la schermata. Se indicate
        un lotto sbagliato, tutto il resto — fasi, quantità, giacenze — sarà perfetto e la
        tracciabilità sarà falsa. Nel dubbio, guardate l'etichetta sul sacco.
      </Callout>

      {/* l3 */}
      <H id="l3">3. Avviare la prima fase</H>

      <P>
        Il pulsante è <Tasto>▶ Avvia prima fase</Tasto>, e si apre la finestra{' '}
        <Strong>"▶ Avvio fase"</Strong> col nome della fase. Tre cose da indicare.
      </P>

      <Passo n={1} titolo="La data e l'ora">
        <P style={{ margin: '6px 0' }}>
          Precompilate con adesso. Si possono correggere se state registrando una lavorazione
          già fatta.
        </P>
      </Passo>
      <Passo n={2} titolo="Dove si svolge">
        <P style={{ margin: '6px 0' }}>
          L'<Strong>ubicazione</Strong>, con le quantità che ci mettete dentro — pezzi, chili, o
          entrambi. È l'informazione che poi vi dirà dove si trova il prodotto.
        </P>
      </Passo>
      <Passo n={3} titolo="Gli ingredienti di questa fase">
        <P style={{ margin: '6px 0' }}>
          Nel riquadro <Strong>"📦 Ingredienti di questa fase"</Strong> ci sono solo quelli che
          la ricetta prevede per questo passaggio, non tutta la lista. Qui si conferma quale
          lotto state usando.
        </P>
      </Passo>

      <P>
        Confermando, la fase risulta in corso e compare il toast <Strong>"✓ Fase
        avviata"</Strong>.
      </P>

      {/* l4 */}
      <H id="l4">4. Dov'è il prodotto adesso</H>

      <P>
        Sulla fase in corso compare il riquadro <Strong>"📍 Posizioni prodotto"</Strong>, con
        l'elenco delle ubicazioni e quanto c'è in ciascuna. È il quadro in tempo reale di dove si
        trova la vostra produzione, ed è la cosa che questa schermata sa fare e un registro
        cartaceo no.
      </P>

      <MockFrame didascalia="La fase in corso. Il riquadro verde è il quadro di dove si trova il prodotto; i comandi accanto a ogni ubicazione servono a muoverlo.">
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg, padding: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.navy, flex: '1 1 auto' }}>
                2 · Stagionatura
              </div>
              <span style={{
                padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                background: LT.bluSoft, color: LT.blu,
              }}>In corso</span>
            </div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 4, marginBottom: 10 }}>
              Iniziata: 17/08/2026 09:30
            </div>

            <div style={{
              background: '#f0faf0', border: '1px solid #c8e6c9',
              borderRadius: LT.r.md, padding: '9px 11px', marginBottom: 10,
            }}>
              <div style={{ fontWeight: 700, color: LT.blu, fontSize: 12.5, marginBottom: 6 }}>
                📍 Posizioni prodotto
              </div>
              {[['Cantina stagionatura', '45 pz / 54,00 kg'],
                ['Magazzino confezionati', '25 pz / 24,00 kg']].map(([u, q]) => (
                <div key={u} style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5,
                  fontSize: 12.5, color: LT.n700,
                }}>
                  <span style={{ flex: 1 }}><Strong>{u}</Strong>: {q}</span>
                  <BtnFinto>📦 Sposta</BtnFinto>
                  <BtnFinto>🗑 Scarta</BtnFinto>
                </div>
              ))}
              <div style={{ marginTop: 7 }}>
                <BtnFinto>+ Aggiungi ubicazione</BtnFinto>
              </div>
            </div>

            <BtnFinto kind="blu">Avvia fase successiva →</BtnFinto>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Accanto a ogni ubicazione ci sono due comandi. <Tasto>📦 Sposta</Tasto> muove del
        prodotto da una cella all'altra: si indicano destinazione, quantità e data.{' '}
        <Tasto>🗑 Scarta</Tasto> toglie del prodotto perché è andato perso, ed è l'unico dei due
        che richiede una <Strong>motivazione obbligatoria</Strong> — perché da uno scarto{' '}
        <Strong>nasce automaticamente una non conformità</Strong>, e la finestra ve lo dice
        prima di confermare.
      </P>
      <P>
        Con <Tasto>+ Aggiungi ubicazione</Tasto> si mette del prodotto in un posto nuovo senza
        toglierlo da un altro: serve quando durante la fase arriva altro materiale.
      </P>

      <Callout kind="info" titolo="Due limiti, entrambi ragionevoli">
        Non si sposta né si scarta <Strong>più di quello che c'è</Strong> nell'ubicazione, né in
        pezzi né in chili. E nessun movimento può essere datato <Strong>prima dell'inizio della
        fase</Strong>: sarebbe successo quando la fase non esisteva. In entrambi i casi la
        finestra resta aperta col messaggio, e correggere costa un attimo.
      </Callout>

      {/* l5 */}
      <H id="l5" cat="manutenzioni">5. Passare alla fase dopo</H>

      <P>
        Qui c'è la cosa che sorprende di più, e conviene dirla chiaramente. Il pulsante si chiama{' '}
        <Tasto>Avvia fase successiva →</Tasto>, ma <Strong>fa due cose insieme</Strong>: chiude
        la fase in corso e apre quella dopo. La finestra che si apre infatti si intitola{' '}
        <Strong>"✓ Chiusura fase"</Strong>.
      </P>
      <P>
        Il nome è scelto dal punto di vista di chi lavora — quello che vuole fare è mandare
        avanti il prodotto — ma se cercate un pulsante "Chiudi fase" non lo trovate, ed è
        normale.
      </P>
      <P>
        Nella finestra si indicano la data e l'ora di fine, i{' '}
        <Strong>parametri rilevati</Strong> se la fase ne prevede, e soprattutto{' '}
        <Strong>quanto prodotto passa avanti e in quale ubicazione</Strong> — è la sezione
        "Ubicazioni di entrata per la fase successiva". Quello che dichiarate qui diventa il
        punto di partenza della fase nuova.
      </P>

      <Callout kind="warn" titolo="Un parametro fuori limite apre una non conformità">
        Se compilate un parametro e il valore esce dai limiti previsti, la finestra vi avverte
        che <Strong>verrà creata automaticamente una Non Conformità</Strong>, e la descrizione
        conterrà il parametro, il valore, i limiti e il lotto. Non è un castigo: è il sistema che
        fa il suo mestiere. Vi resta da compilare l'azione correttiva nel registro NC.
      </Callout>

      <P>
        Fra quello che entra in una fase e quello che ne esce c'è quasi sempre una differenza —
        l'acqua che evapora, gli scarti di rifilatura. È il <Strong>calo</Strong>, ed è normale:
        il programma non si lamenta, lo registra e lo mostrerà nel bilancio di massa.
      </P>

      {/* l6 */}
      <H id="l6">6. Le quantità usate</H>

      <P>
        Nel riquadro <Strong>"📦 MP / MOCA utilizzate"</Strong> ogni lotto agganciato ha un campo{' '}
        <Strong>usata</Strong>: quanto ne avete consumato davvero. Si può compilare durante la
        lavorazione o dopo, e si può correggere.
      </P>
      <P>
        Questa è una scelta di fondo di LabThree e vale la pena conoscerla:{' '}
        <Strong>questa quantità non blocca mai niente</Strong>. Potete dichiarare di aver usato
        più di quanto risultasse a magazzino, potete lasciarla vuota, potete cambiarla domani. In
        un laboratorio vero i numeri non tornano al grammo, e un programma che pretendesse il
        contrario verrebbe aggirato con dati inventati — che è molto peggio di un dato
        approssimato.
      </P>
      <P>
        Se la lasciate vuota, il bilancio di massa userà la stima dalla ricetta. Compilarla lo
        rende molto più preciso, ma è una vostra scelta, non un obbligo.
      </P>

      {/* l7 */}
      <H id="l7" cat="formazione">7. Chiudere la lavorazione</H>

      <P>
        Quando siete all'ultima fase, il pulsante <Tasto>✅ Chiusura lavorazione</Tasto> apre la
        finestra finale. Ha due parti.
      </P>
      <P>
        <Strong>"📦 Dati finali lavorazione"</Strong>: quanto avete prodotto davvero, in pezzi e
        in chili. È il numero che finisce sul lotto di prodotto finito.
      </P>
      <P>
        <Strong>"📍 Dove resta il prodotto"</Strong>: in quali ubicazioni si trova adesso. Le
        righe arrivano già compilate con quello che risulta nell'ultima fase, e si possono
        correggere, togliere o aggiungere.
      </P>

      <MockFrame didascalia="La finestra di chiusura. I due totali devono coincidere: quello che dichiari di aver prodotto è quello che resta fisicamente nelle celle.">
        <Schermo style={{ padding: 18, background: LT.n100 }}>
          <div style={{
            maxWidth: 420, margin: '0 auto', background: LT.n0,
            borderRadius: LT.r.lg, border: `1px solid ${LT.n200}`, padding: 16, boxShadow: LT.sh2,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: LT.navy, marginBottom: 12 }}>
              🏁 Chiusura Lavorazione
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: LT.n700, marginBottom: 7 }}>
              📦 Dati finali lavorazione
            </div>
            <div style={{ display: 'flex', gap: 9, marginBottom: 13 }}>
              <CampoFinto etichetta="Pezzi prodotti" valore="44" larghezza="50%" />
              <CampoFinto etichetta="Peso totale (kg)" valore="40" larghezza="50%" />
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: LT.n700, marginBottom: 7 }}>
              📍 Dove resta il prodotto
            </div>
            {[['Cantina stagionatura', '20', '20'], ['Magazzino confezionati', '24', '20']].map(([u, pz, kg]) => (
              <div key={u} style={{
                display: 'flex', gap: 7, alignItems: 'flex-end', marginBottom: 7,
              }}>
                <CampoFinto etichetta="Ubicazione" valore={u} larghezza="50%" />
                <CampoFinto etichetta="pz" valore={pz} larghezza="22%" />
                <CampoFinto etichetta="kg" valore={kg} larghezza="22%" />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}><BtnFinto>+ Aggiungi riga</BtnFinto></div>
            <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              <BtnFinto>Annulla</BtnFinto>
              <BtnFinto kind="verde">✓ Conferma</BtnFinto>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="warn" titolo="I due totali devono coincidere">
        La somma delle righe deve corrispondere ai dati finali dichiarati. Se non coincidono il
        programma <Strong>non chiude</Strong> e vi dice di quanto siete lontani. Non è pignoleria:
        quello che dichiarate di aver prodotto è quello che resta fisicamente nelle celle, e
        lasciar passare uno scostamento vorrebbe dire scrivere in giacenza un numero diverso da
        quello dichiarato, senza che nessuno se ne accorga più.
      </Callout>

      <P>
        Se una parte del prodotto è andata persa, la strada è <Strong>abbassare le righe</Strong>{' '}
        fino ad arrivare ai dati finali: la differenza viene registrata da sola come calo di
        lavorazione, e le ubicazioni tornano a zero. C'è anche un pulsante che allinea le righe ai
        dati finali in un colpo solo.
      </P>
      <P>
        Alla conferma nasce il <Strong>lotto di prodotto finito</Strong>, che entra nel magazzino
        prodotti finiti ed è pronto per essere spedito con una bolla di uscita.
      </P>

      <Callout kind="info" titolo="Le fasi non si saltano">
        La chiusura controlla che tutte le fasi della ricetta siano state fatte e che non ne resti
        più di una aperta. Se manca qualcosa il programma vi elenca <Strong>quali</Strong> fasi,
        così sapete dove tornare. La chiusura finale chiude da sé l'ultima fase.
      </Callout>

      {/* l8 */}
      <H id="l8">8. Se qualcosa va storto</H>

      <P>
        <Strong>Lavorazione aperta per sbaglio.</Strong> C'è{' '}
        <Tasto>🚫 Annulla lavorazione</Tasto>, che chiede una motivazione e la mette in stato
        Annullata. Funziona <em>solo</em> finché è aperta.
      </P>
      <P>
        <Strong>Lavorazione chiusa troppo presto.</Strong> Non si riapre. La strada è una
        rettifica sul magazzino prodotti finiti, dove le quantità si possono correggere
        indicando il motivo.
      </P>
      <P>
        <Strong>Lotto sbagliato.</Strong> Finché la lavorazione è aperta si può togliere la riga
        e mettere quella giusta. Se è già chiusa, si apre una non conformità che documenti
        l'errore: è il modo corretto di correggere un registro, cioè scriverci sopra, non
        cancellare.
      </P>

      {/* l9 */}
      <H id="l9">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['Non esiste un pulsante "Chiudi fase"',
            'Si chiama "Avvia fase successiva →" e fa entrambe le cose: chiude quella in corso e apre la dopo. La finestra che si apre si intitola "Chiusura fase".'],
          ['Una lavorazione chiusa non si riapre',
            'Il messaggio è "operazione non ripetibile". Prima di confermare la chiusura, controlla i dati finali.'],
          ['Alla chiusura i totali devono coincidere',
            'Se la somma delle ubicazioni non corrisponde ai dati finali, la chiusura si ferma. Abbassa le righe di quanto è andato perso: la differenza diventa calo da sola.'],
          ['Lo scarto crea una non conformità',
            'Sempre, e per questo chiede la motivazione. Non usarlo per correggere un errore di battitura: per quello si sistema la riga.'],
          ['Le quantità usate non bloccano mai',
            'Puoi dichiarare più di quanto risulti a magazzino. È voluto — ma vuol dire che nessuno ti avvisa se sbagli a scrivere.'],
          ['Il calo fra una fase e l\'altra è normale',
            "Quello che entra e quello che esce non coincidono quasi mai. Il programma lo registra e lo mostra nel bilancio di massa: non è un errore da correggere."],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* l10 */}
      <H id="l10">Domande frequenti</H>

      <FAQ q="Posso registrare una lavorazione di ieri?">
        Sì. Le date di inizio, di avvio e chiusura delle fasi e dei movimenti si indicano a mano,
        quindi si può mettere in ordine anche una produzione già fatta. L'unico limite è di buon
        senso e il programma lo applica: un movimento non può essere datato prima dell'inizio
        della sua fase.
      </FAQ>

      <FAQ q="Devo per forza usare le ubicazioni?">
        Almeno una sì, perché è così che il sistema sa dove si trova il prodotto e perché alla
        chiusura vi chiede dove resta. Ma se la vostra produzione sta tutta in un posto, una sola
        ubicazione usata sempre va benissimo.
      </FAQ>

      <FAQ q="Cosa succede se lascio una lavorazione aperta per settimane?">
        Niente di male: resta aperta e continua a comparire fra quelle in corso, e le sue giacenze
        restano visibili. L'unico effetto è che il prodotto risulta ancora in lavorazione invece
        che a magazzino. Le lavorazioni dimenticate si riconoscono dalla Dashboard, che conta
        quelle aperte.
      </FAQ>

      <FAQ q="Posso usare due lotti diversi dello stesso ingrediente?">
        Sì, si agganciano entrambi alla lavorazione. È il caso normale quando un lotto finisce a
        metà produzione, ed è anche il motivo per cui la tracciabilità funziona: in caso di
        richiamo risulteranno coinvolti tutti e due.
      </FAQ>

      <FAQ q="Il peso finale è meno della somma degli ingredienti: ho sbagliato qualcosa?">
        Quasi certamente no. Fra materie prime e prodotto finito c'è sempre un calo — evaporazione,
        rifilature, scarti — e in certe lavorazioni è molto rilevante. Il bilancio di massa serve
        proprio a misurarlo: se il valore è nella norma per il vostro prodotto, è tutto a posto.
      </FAQ>

      <FAQ q="Due lavorazioni avviate lo stesso giorno hanno lo stesso lotto. È un problema?">
        Non è un errore, il lotto nasce dalla data. Se vi serve distinguerle, personalizzate il
        codice quando aprite la lavorazione, per esempio aggiungendo un suffisso. Nelle schermate
        dove conta, il programma mostra comunque anche il numero della lavorazione.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Fai una lavorazione di prova dall'inizio alla fine, anche piccola: aprila, avvia una
          fase, sposta qualcosa, chiudila. Quando l'hai portata a termine una volta, hai capito il
          modulo — e il lotto che ne esce ti aspetta nel{' '}
          <Strong>magazzino prodotti finiti</Strong>, pronto per la prima bolla di uscita.
        </div>
      </div>

    </GuidaLayout>
  );
}
